/**
 * Handlers of the Gleap "Frontend tools" Tony (the Gleap AI agent) calls from
 * the support widget. Gleap holds the tool definitions (dashboard, see
 * ./README.md); this file only answers them, from the merchant's browser and
 * with the merchant's own Clerk session, so a tool can read nothing the
 * merchant's screens cannot.
 *
 * Every handler returns the server's JSON as is, a neutral `unavailable`
 * answer when the tool is switched off on the server, or a hand-off when the
 * call fails: never an error message, a status code or a stack.
 */

/**
 * Said when a call fails. Same sentence as the servers' gate hand-off
 * (`HANDOFF_SAY` in src/app/useCases/support/supportTools.ts and in
 * payment-stack), so a 403, an error and a gate read the same to the merchant.
 */
export const FALLBACK_SAY = 'I can\'t help with this here. Please tap "Talk to our support team" below.';

/**
 * A tool switched off on the server (`SUPPORT_TOOLS`) is not a failure: Tony
 * must keep answering how-to questions from the help center instead of handing
 * every data question to the team. The help center is no licence to explain an
 * account's situation, hence the reminder.
 */
export const UNAVAILABLE_SAY =
  'This tool is not available. Answer how-to questions from the help center. All your rules still apply: no causes, no consequences, no durations.';

/** Raised inside a handler when the server says the tool is switched off. */
class ToolDisabled extends Error {}

const unavailable = (tool: AgentToolName) => ({ ok: false, tool, unavailable: true, say: UNAVAILABLE_SAY });

/**
 * A failed call reads exactly like the servers' gate hand-off
 * (`SupportToolHandoff` in src/app/useCases/support/supportTools.ts), so
 * Tony's "no more tools after a hand-off" rule fires on an error too.
 */
const handoff = (tool: AgentToolName) => ({ ok: true, tool, handoff: true, say: FALLBACK_SAY });

/** tRPC answers a switched-off tool with NOT_FOUND `tool_disabled` (src/app/useCases/support/supportTools.ts). */
function isTrpcToolDisabled(error: unknown): boolean {
  if (!isObject(error)) return false;
  const { message, data } = error as { message?: unknown; data?: { code?: unknown } };
  return message === 'tool_disabled' && data?.code === 'NOT_FOUND';
}

/** Tool name in the Gleap dashboard → path under payment-stack's /api/v2/merchant/support/. */
export const PAYMENT_STACK_TOOLS = {
  tagada_account: 'account',
  tagada_money: 'money',
  tagada_transactions: 'transactions',
  tagada_requests: 'requests',
} as const;

export type PaymentStackToolName = keyof typeof PAYMENT_STACK_TOOLS;
export type CrmToolName = 'tagada_billing' | 'tagada_stores';
export type AgentToolName = PaymentStackToolName | CrmToolName;

/** The only parameters forwarded, whatever Tony sends. */
const PAYMENT_STACK_PARAMS = ['tpa_id', 'entity_id', 'q', 'from', 'to'] as const;
const MAX_PARAM_LENGTH = 200;

export type AgentToolResult = object;
export type AgentToolHandler = (params: Record<string, unknown>) => Promise<AgentToolResult>;

export interface AgentToolDeps {
  /** Clerk session token of the signed-in merchant. */
  getToken: () => Promise<string | null>;
  /** payment-stack origin, e.g. https://api.tagadapay.io. */
  paymentStackUrl: string;
  fetch: typeof fetch;
  /** tRPC `support.billing` and `support.stores` of the CRM API. */
  readBilling: (input: { from?: string; to?: string }) => Promise<unknown>;
  readStores: (input: { q?: string }) => Promise<unknown>;
}

/** Keep only the listed keys whose value is a non-empty string, trimmed and capped. */
export function pickStringParams<K extends string>(
  params: Record<string, unknown> | undefined,
  keys: readonly K[],
): Partial<Record<K, string>> {
  return keys.reduce<Partial<Record<K, string>>>((picked, key) => {
    const value = params?.[key];
    if (typeof value !== 'string') return picked;
    const trimmed = value.trim().slice(0, MAX_PARAM_LENGTH);
    return trimmed ? { ...picked, [key]: trimmed } : picked;
  }, {});
}

const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null;

async function safely(tool: AgentToolName, read: () => Promise<unknown>): Promise<AgentToolResult> {
  try {
    const result = await read();
    return isObject(result) ? result : handoff(tool);
  } catch (error) {
    return error instanceof ToolDisabled || isTrpcToolDisabled(error) ? unavailable(tool) : handoff(tool);
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function paymentStackHandler(deps: AgentToolDeps, tool: PaymentStackToolName): AgentToolHandler {
  return (params) =>
    safely(tool, async () => {
      const token = await deps.getToken();
      if (!token) return null;
      const query = new URLSearchParams(pickStringParams(params, PAYMENT_STACK_PARAMS)).toString();
      const url = `${deps.paymentStackUrl}/api/v2/merchant/support/${PAYMENT_STACK_TOOLS[tool]}${query ? `?${query}` : ''}`;
      const response = await deps.fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (response.ok) return response.json();
      // A switched-off tool answers 404 { error: "tool_disabled" }; every other failure hands off.
      const body = response.status === 404 ? await readJson(response) : null;
      if (isObject(body) && (body as { error?: unknown }).error === 'tool_disabled') throw new ToolDisabled();
      return null;
    });
}

export function createAgentToolHandlers(deps: AgentToolDeps): Record<AgentToolName, AgentToolHandler> {
  return {
    tagada_account: paymentStackHandler(deps, 'tagada_account'),
    tagada_money: paymentStackHandler(deps, 'tagada_money'),
    tagada_transactions: paymentStackHandler(deps, 'tagada_transactions'),
    tagada_requests: paymentStackHandler(deps, 'tagada_requests'),
    tagada_billing: (params) =>
      safely('tagada_billing', () => deps.readBilling(pickStringParams(params, ['from', 'to'] as const))),
    tagada_stores: (params) =>
      safely('tagada_stores', () => deps.readStores(pickStringParams(params, ['q'] as const))),
  };
}

/**
 * The key Gleap calls a Frontend tool by. Gleap derives it from the tool's
 * name in the dashboard by turning `_` into `-`: the tool `tagada_account`
 * runs the handler registered as `tagada-account`. The `tool` field of every
 * answer and log keeps the dashboard name.
 */
export const gleapRuntimeKey = (tool: AgentToolName): string => tool.replace(/_/g, '-');

export function registerAgentTools(
  deps: AgentToolDeps,
  register: (name: string, handler: AgentToolHandler) => void,
): void {
  for (const [tool, handler] of Object.entries(createAgentToolHandlers(deps))) {
    register(gleapRuntimeKey(tool as AgentToolName), handler);
  }
}
